// PWA Service for managing service worker and app installation

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

export interface PWAInstallState {
  isInstallable: boolean
  isInstalled: boolean
  isStandalone: boolean
  canInstall: boolean
  platform: 'ios' | 'android' | 'desktop' | 'unknown'
}

export interface ServiceWorkerState {
  isSupported: boolean
  isRegistered: boolean
  isActivated: boolean
  registration: ServiceWorkerRegistration | null
  hasUpdate: boolean
}

class PWAService {
  private installPrompt: BeforeInstallPromptEvent | null = null
  private updateAvailableCallback: (() => void) | null = null
  private installPromptCallback: ((canInstall: boolean) => void) | null = null
  
  // Initialize PWA functionality
  async initialize(): Promise<void> {
    this.setupInstallPromptListener()
    this.setupAppInstalledListener() 
    await this.registerServiceWorker()
  }

  // Service Worker Management
  async registerServiceWorker(): Promise<ServiceWorkerState> {
    if (!('serviceWorker' in navigator)) {
      console.log('[PWA] Service Worker not supported')
      return {
        isSupported: false,
        isRegistered: false,
        isActivated: false,
        registration: null,
        hasUpdate: false
      }
    }

    try {
      console.log('[PWA] Registering service worker')
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      })

      console.log('[PWA] Service worker registered:', registration.scope)

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        console.log('[PWA] Service worker update found')
        const newWorker = registration.installing

        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[PWA] New service worker installed, update available')
              if (this.updateAvailableCallback) {
                this.updateAvailableCallback()
              }
            }
          })
        }
      })

      // Handle controller change (new SW activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[PWA] Service worker controller changed')
        window.location.reload()
      })

      return {
        isSupported: true,
        isRegistered: true,
        isActivated: registration.active !== null,
        registration,
        hasUpdate: registration.waiting !== null
      }

    } catch (error) {
      console.error('[PWA] Service worker registration failed:', error)
      return {
        isSupported: true,
        isRegistered: false,
        isActivated: false,
        registration: null,
        hasUpdate: false
      }
    }
  }

  // Activate waiting service worker
  async activateUpdate(): Promise<void> {
    const registration = await navigator.serviceWorker.getRegistration()
    
    if (registration?.waiting) {
      console.log('[PWA] Activating waiting service worker')
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  }

  // App Installation Management
  getPWAInstallState(): PWAInstallState {
    const isStandalone = this.isStandalone()
    const platform = this.detectPlatform()
    const isInstalled = this.isAppInstalled()
    
    return {
      isInstallable: this.installPrompt !== null,
      isInstalled,
      isStandalone,
      canInstall: this.installPrompt !== null && !isInstalled,
      platform
    }
  }

  async promptInstall(): Promise<boolean> {
    if (!this.installPrompt) {
      console.log('[PWA] Install prompt not available')
      return false
    }

    try {
      console.log('[PWA] Showing install prompt')
      await this.installPrompt.prompt()
      
      const result = await this.installPrompt.userChoice
      console.log('[PWA] Install prompt result:', result.outcome)
      
      this.installPrompt = null
      
      if (this.installPromptCallback) {
        this.installPromptCallback(false)
      }
      
      return result.outcome === 'accepted'
    } catch (error) {
      console.error('[PWA] Install prompt failed:', error)
      return false
    }
  }

  // Event Listeners Setup
  private setupInstallPromptListener(): void {
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      console.log('[PWA] Before install prompt event')
      event.preventDefault()
      
      this.installPrompt = event as BeforeInstallPromptEvent
      
      if (this.installPromptCallback) {
        this.installPromptCallback(true)
      }
    })
  }

  private setupAppInstalledListener(): void {
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App was installed')
      this.installPrompt = null
      
      if (this.installPromptCallback) {
        this.installPromptCallback(false)
      }
      
      // Track installation
      this.trackInstallation()
    })
  }

  // Platform Detection
  private detectPlatform(): 'ios' | 'android' | 'desktop' | 'unknown' {
    const userAgent = navigator.userAgent.toLowerCase()
    
    if (/ipad|iphone|ipod/.test(userAgent)) {
      return 'ios'
    } else if (/android/.test(userAgent)) {
      return 'android'
    } else if (/windows|macintosh|linux/.test(userAgent)) {
      return 'desktop'
    }
    
    return 'unknown'
  }

  private isStandalone(): boolean {
    // Check if running as standalone PWA
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://')
    )
  }

  private isAppInstalled(): boolean {
    // Check various indicators of installation
    return (
      this.isStandalone() ||
      localStorage.getItem('pwa-installed') === 'true'
    )
  }

  // Offline Functionality
  async storeOfflineData(key: string, data: any): Promise<void> {
    try {
      const offlineStore = await this.getOfflineStore()
      await offlineStore.put(key, {
        data,
        timestamp: Date.now()
      })
    } catch (error) {
      console.error('[PWA] Failed to store offline data:', error)
    }
  }

  async getOfflineData(key: string): Promise<any | null> {
    try {
      const offlineStore = await this.getOfflineStore()
      const result = await offlineStore.get(key)
      return result?.data || null
    } catch (error) {
      console.error('[PWA] Failed to get offline data:', error)
      return null
    }
  }

  async clearOfflineData(): Promise<void> {
    try {
      const offlineStore = await this.getOfflineStore()
      await offlineStore.clear()
    } catch (error) {
      console.error('[PWA] Failed to clear offline data:', error)
    }
  }

  private async getOfflineStore(): Promise<IDBObjectStore> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('clokd-offline', 1)
      
      request.onerror = () => reject(request.error)
      
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['offline-data'], 'readwrite')
        resolve(transaction.objectStore('offline-data'))
      }
      
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains('offline-data')) {
          db.createObjectStore('offline-data')
        }
      }
    })
  }

  // Push Notifications
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.log('[PWA] Notifications not supported')
      return 'denied'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission === 'denied') {
      return 'denied'
    }

    const permission = await Notification.requestPermission()
    console.log('[PWA] Notification permission:', permission)
    
    return permission
  }

  async subscribeToPushNotifications(): Promise<PushSubscription | null> {
    const registration = await navigator.serviceWorker.getRegistration()
    
    if (!registration) {
      console.log('[PWA] No service worker registration found')
      return null
    }

    try {
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.getVapidPublicKey()
      })

      console.log('[PWA] Push subscription created')
      return subscription
    } catch (error) {
      console.error('[PWA] Push subscription failed:', error)
      return null
    }
  }

  private getVapidPublicKey(): Uint8Array {
    // This would be your actual VAPID public key
    const vapidPublicKey = 'YOUR_VAPID_PUBLIC_KEY_HERE'
    return new Uint8Array(
      atob(vapidPublicKey.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map(char => char.charCodeAt(0))
    )
  }

  // Callbacks
  onUpdateAvailable(callback: () => void): void {
    this.updateAvailableCallback = callback
  }

  onInstallPromptReady(callback: (canInstall: boolean) => void): void {
    this.installPromptCallback = callback
  }

  // Analytics/Tracking
  private trackInstallation(): void {
    localStorage.setItem('pwa-installed', 'true')
    localStorage.setItem('pwa-install-date', new Date().toISOString())
    
    // Send analytics event
    if ('gtag' in window) {
      (window as any).gtag('event', 'pwa_install', {
        event_category: 'engagement',
        event_label: this.detectPlatform()
      })
    }
  }

  // Check for updates manually
  async checkForUpdates(): Promise<boolean> {
    const registration = await navigator.serviceWorker.getRegistration()
    
    if (registration) {
      await registration.update()
      return registration.waiting !== null
    }
    
    return false
  }

  // Get app version info
  async getVersion(): Promise<string> {
    const registration = await navigator.serviceWorker.getRegistration()
    
    if (registration?.active) {
      return new Promise((resolve) => {
        const messageChannel = new MessageChannel()
        messageChannel.port1.onmessage = (event) => {
          resolve(event.data.version || 'unknown')
        }
        
        registration.active.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2])
      })
    }
    
    return 'unknown'
  }
}

export const pwaService = new PWAService()